"use client";

import { useState } from "react";
import { Users, AlertTriangle, X, CheckCircle, FileWarning, AlertCircle } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ParticipantTable from "@/components/private/pic/ParticipantTable";
import ExportButton from "@/components/private/pic/ExportButton";

// ✅ Import Button & Tipe Data
import ImportButton from "@/components/private/pic/ImportButton";
import type { ImportResponse } from "@/components/private/pic/ImportModal";
interface ImportDataPaketProps {
    onDataChange?: (count: number) => void;
}

export default function ImportDataPaket({ onDataChange }: ImportDataPaketProps) {
    const [reloadKey, setReloadKey] = useState(0);
    // ✅ State typed dengan ImportResponse
    const [importResult, setImportResult] = useState<ImportResponse | null>(null);

    // ✅ Handler typed
    const handleImported = (result: ImportResponse): void => {
        setImportResult(result);
        setReloadKey((prev) => prev + 1);

        // Scroll ke atas agar notifikasi terlihat
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const closeNotification = () => {
        setImportResult(null);
    };



    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* === HEADER === */}
            <Card className="border-0 shadow-md bg-white dark:bg-gray-900 overflow-hidden">
                <CardHeader className="pb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <CardTitle className="text-2xl font-bold flex items-center gap-2 text-gray-900 dark:text-gray-50">
                            <Users className="w-6 h-6 text-blue-600" />
                            Kelola Peserta Kolektif
                        </CardTitle>
                        <CardDescription className="text-gray-500 dark:text-gray-400 mt-1">
                            Impor data peserta kolektif, lihat status pembayaran, dan kelola daftar peserta Anda.
                        </CardDescription>
                    </div>

                    <div className="flex items-center gap-3">
                        <ImportButton onImported={handleImported} />
                        <ExportButton endpoint="/pic/participants/export" filenamePrefix="peserta_kolektif" />
                    </div>
                </CardHeader>
            </Card>

            {/* === NOTIFIKASI HASIL IMPORT (Muncul jika ada result) === */}
            {importResult && (
                <div className="relative overflow-hidden rounded-xl border border-l-4 shadow-sm animate-in slide-in-from-top-4 duration-300 
                    bg-white dark:bg-gray-900 
                    border-l-blue-500 
                    border-gray-200 dark:border-gray-800"
                >
                    {/* Header Notifikasi */}
                    <div className="p-4 flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            {importResult.skipped_count > 0 ? (
                                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                            ) : (
                                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full text-green-600">
                                    <CheckCircle className="w-5 h-5" />
                                </div>
                            )}

                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                    Proses Import Selesai
                                </h3>
                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        {importResult.imported_count} Berhasil
                                    </span>
                                    {importResult.skipped_count > 0 && (
                                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-500 font-medium">
                                            <FileWarning className="w-3.5 h-3.5" />
                                            {importResult.skipped_count} Dilewati/Gagal
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Tombol Close */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={closeNotification}
                            className="text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 -mt-1 -mr-1"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Detail Gagal (Hanya jika ada) */}
                    {importResult.skipped_count > 0 && (
                        <div className="bg-amber-50/50 dark:bg-amber-900/10 border-t border-amber-100 dark:border-amber-900/20 p-4">
                            <p className="text-xs font-semibold text-amber-800 dark:text-amber-500 mb-2 flex items-center gap-2">
                                <AlertCircle className="w-3.5 h-3.5" />
                                Daftar data yang tidak diimpor:
                            </p>

                            <ScrollArea className="h-32 w-full rounded-md border border-amber-200/60 dark:border-amber-800/30 bg-white/50 dark:bg-gray-800/50 p-2">
                                <ul className="space-y-2">
                                    {importResult.skipped.map((item, idx) => (
                                        <li key={idx} className="text-xs flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 pb-0 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors">
                                            <span className="font-mono font-bold text-gray-700 dark:text-gray-300 min-w-[150px]">
                                                {item.email}
                                            </span>
                                            <span className="text-amber-700 dark:text-amber-400">
                                                — {item.reason}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </ScrollArea>
                        </div>
                    )}
                </div>
            )}

            {/* === TABEL DATA === */}
            <div className="w-full overflow-x-auto">
                <ParticipantTable reloadTrigger={reloadKey} onDataLoaded={onDataChange} />
            </div>
        </div>
    );
}